import React from 'react';
import PageHero from '@/components/site/PageHero';
import SectionHeader from '@/components/site/SectionHeader';
import ClosingCta from '@/components/site/ClosingCta';
import GoldText from '@/components/site/GoldText';
import { usePageMeta } from '@/hooks/usePageMeta';

const areas = [
  ['Performance review', 'Review agreed measures, exceptions, operating costs, and user feedback.'],
  [
    'Improvement planning',
    'Maintain a prioritized list of changes, with clear reasons for doing—or deferring—each item.',
  ],
  [
    'Adoption and accountability',
    'Help establish ownership, review procedures, training needs, and expectations for the people using the assistant.',
  ],
  ['Future implementations', 'Assess new workflow opportunities before committing to another build.'],
];

const rhythm = [
  ['Review', 'Look back at the previous period: measures, exceptions, costs, and feedback.'],
  ['Decide', 'Identify the decisions that need to be made.'],
  ['Prioritize', 'Agree which improvements to make next, and which to defer.'],
  ['Assign', 'Confirm responsibilities for the next period.'],
];

const Consulting = () => {
  usePageMeta(
    'Ongoing Consulting',
    'Ongoing AI consulting for performance review, adoption, and future implementation decisions.',
  );

  return (
    <>
      <PageHero
        eyebrow="Ongoing consulting"
        interest="consulting"
        title={
          <>
            Keep improving what works.
            <br />
            <GoldText>Be selective about what comes next.</GoldText>
          </>
        }
      >
        <p>
          Ongoing AI consulting for businesses that want a practical partner for performance review, adoption, and
          future implementation decisions.
        </p>
      </PageHero>

      <section aria-label="Consulting areas" className="border-t border-rule">
        <div className="section-container">
          <ul className="grid gap-4 sm:grid-cols-2">
            {areas.map(([title, body], i) => (
              <li key={title} className="surface p-6 sm:p-8">
                <p className="mb-4 font-mono text-xs tracking-[0.18em]">
                  <span className="gold-shine">0{i + 1}</span>
                </p>
                <h2 className="mb-2 text-2xl">{title}</h2>
                <p className="text-mist">{body}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section aria-labelledby="rhythm-title" className="border-t border-rule">
        <div className="section-container grid gap-12 lg:grid-cols-[0.8fr_1.2fr]">
          <SectionHeader id="rhythm-title" title="Engagement structure">
            <p>
              A proposed monthly rhythm. Cadence, deliverables, and fees are agreed with you before the engagement
              starts.
            </p>
          </SectionHeader>
          <ol className="grid gap-4 sm:grid-cols-2">
            {rhythm.map(([step, body], i) => (
              <li key={step} className="border-l-2 border-goldcore/60 pl-5">
                <p className="meta-label mb-1">Step {i + 1}</p>
                <h3 className="mb-1 text-xl">{step}</h3>
                <p className="text-mist">{body}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section aria-labelledby="distinction-title" className="border-t border-rule">
        <div className="mx-auto max-w-4xl px-4 py-16 sm:px-6">
          <h2 id="distinction-title" className="eyebrow mb-4 font-mono">
            What consulting covers
          </h2>
          <p className="font-serif text-3xl leading-snug sm:text-4xl">
            Consulting provides advice, review, and agreed support. New development, hosting, incident response, and
            maintenance are separate unless included in your engagement.
          </p>
        </div>
      </section>

      <ClosingCta
        interest="consulting"
        title={
          <>
            A clearer plan <GoldText>for AI in your business.</GoldText>
          </>
        }
      />
    </>
  );
};

export default Consulting;
